/**
 * Formatting helpers shared across the report's sections.
 *
 * Отчёт приходит в исходных величинах: деньги — числом в валюте отчёта,
 * даты — ISO. Всё, что читатель видит словами, набирается здесь: склонение,
 * разряды, знак валюты, «7 июля 2026» вместо «07.07.2026».
 *
 * @format
 */

import {buildFormatter, buildFormatterObject} from 'helpers/numberHelpers';

import {surfaceVar} from 'ui/theme/surfaceContext';

import type {Amount, IsoDate, Target, Unit} from './types';

/** Язык, на котором написан отчёт. Он же задаёт разряды и склонения. */
export const LOCALE = 'ru-RU';

/** Неразрывный пробел: держит предлог при следующем слове (typography.md §2). */
const NBSP = '\u00A0';

/**
 * Pick the right Russian plural form for a count.
 *
 * Forms are ordered [1, 2, 5] — «балл» / «балла» / «баллов». Russian keys
 * off the last two digits: 11–14 always take the many-form regardless of
 * their final digit, which is why "12 баллов" is right and "12 балла" reads
 * as broken.
 *
 *     plural(3, ['день', 'дня', 'дней'])  // 'дня'
 *     plural(12, ['балл', 'балла', 'баллов'])  // 'баллов'
 */
export function plural(count: number, forms: [string, string, string]): string {
  const n = Math.abs(count) % 100;
  if (n >= 11 && n <= 14) return forms[2];
  const last = n % 10;
  if (last === 1) return forms[0];
  if (last >= 2 && last <= 4) return forms[1];
  return forms[2];
}

/**
 * Количество с разрядами: `1067` → `1 067`.
 *
 * У денег форматтер свой, а количества шли в разметку сырыми — и на
 * боевом кабинете «1067 получено» читалось как код, а не как число.
 */
export function count(value: number): string {
  return value.toLocaleString(LOCALE);
}

/** `12` → `'12 баллов'`. */
export function pluralize(count: number, forms: [string, string, string]): string {
  return `${count.toLocaleString(LOCALE)} ${plural(count, forms)}`;
}

export const DAYS: [string, string, string] = ['день', 'дня', 'дней'];
export const POINTS: [string, string, string] = ['балл', 'балла', 'баллов'];
export const CAMPAIGNS: [string, string, string] = ['кампания', 'кампании', 'кампаний'];
export const TASKS: [string, string, string] = ['задача', 'задачи', 'задач'];
export const ENQUIRIES: [string, string, string] = ['обращение', 'обращения', 'обращений'];
export const EDITS: [string, string, string] = ['правка', 'правки', 'правок'];
export const OPERATIONS: [string, string, string] = ['операция', 'операции', 'операций'];
export const CLICKS: [string, string, string] = ['клик', 'клика', 'кликов'];

/** Месяцы в родительном падеже: дата всегда читается как «7 июля». */
export const MONTHS_GENITIVE = [
  'января',
  'февраля',
  'марта',
  'апреля',
  'мая',
  'июня',
  'июля',
  'августа',
  'сентября',
  'октября',
  'ноября',
  'декабря',
];

/** Месяцы в именительном — подпись оси и колонки: «март», а не «марта». */
export const MONTHS_NOMINATIVE = [
  'январь',
  'февраль',
  'март',
  'апрель',
  'май',
  'июнь',
  'июль',
  'август',
  'сентябрь',
  'октябрь',
  'ноябрь',
  'декабрь',
];

interface DateParts {
  year: number;
  month: number;
  day: number;
}

/**
 * Разбор ISO-даты по компонентам.
 *
 * Именно по компонентам, а не `new Date(iso)`: тот читает `ГГГГ-ММ-ДД` как
 * UTC-полночь, и дальше любое обращение к локальным геттерам зависит от
 * зоны исполнения. Календарной дате зона не нужна вовсе — «6 июля» это
 * шестое июля и в Москве, и где угодно ещё.
 *
 * Непригодный вход отдаём как `null`: подменять его выдуманной датой хуже,
 * чем показать исходную строку.
 */
export function parseIsoDate(date: IsoDate): DateParts | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!match) return null;
  const [, year, month, day] = match;
  const parts = {year: Number(year), month: Number(month), day: Number(day)};
  if (parts.month < 1 || parts.month > 12 || parts.day < 1 || parts.day > 31) return null;
  return parts;
}

/** ISO-дата → полночь в текущей зоне. Для перебора дней и сравнений. */
export function toLocalDate(date: IsoDate): Date | null {
  const parts = parseIsoDate(date);
  if (!parts) return null;
  return new Date(parts.year, parts.month - 1, parts.day);
}

/** `Date` → ISO `ГГГГ-ММ-ДД` по локальным полям, без ухода в UTC. */
export function toIsoDate(date: Date): IsoDate {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

/**
 * `2026-07-07` → `7 июля 2026`.
 *
 * Числовая дата — формат для машины: её читают по разрядам, а «07.07» ещё и
 * заставляет проверять, где день, а где месяц. В шапке отчёта дату читают
 * один раз и словами.
 */
export function longDate(date: IsoDate): string {
  const parts = parseIsoDate(date);
  if (!parts) return date;
  return `${parts.day} ${MONTHS_GENITIVE[parts.month - 1]} ${parts.year}`;
}

/** `2026-07-07` → `7 июля`. Год опускается там, где он и так известен. */
export function dayMonth(date: IsoDate): string {
  const parts = parseIsoDate(date);
  if (!parts) return date;
  return `${parts.day} ${MONTHS_GENITIVE[parts.month - 1]}`;
}

/** `2026-03-01` → `01.03.2026`. Компактная форма для строки реквизитов. */
export function shortDate(date: IsoDate): string {
  const parts = parseIsoDate(date);
  if (!parts) return date;
  const day = String(parts.day).padStart(2, '0');
  const month = String(parts.month).padStart(2, '0');
  return `${day}.${month}.${parts.year}`;
}

/** `2026-03-15` → `март`. Подпись месяца, именительный падеж. */
export function monthName(date: IsoDate): string {
  const parts = parseIsoDate(date);
  if (!parts) return date;
  return MONTHS_NOMINATIVE[parts.month - 1];
}

/** То же с заглавной — когда месяц стоит заголовком колонки. */
export function monthTitle(date: IsoDate): string {
  const name = monthName(date);
  return name.charAt(0).toUpperCase() + name.slice(1);
}

/**
 * Границы отрезка одной строкой: «18–30 апреля», «28 апреля — 3 мая»,
 * «30 декабря 2025 — 3 января 2026».
 *
 * Общий месяц называется один раз — повтор («18 апреля – 30 апреля») глаз
 * читает как два независимых события, хотя это один отрезок. Внутри месяца
 * границы разделяет короткое тире без пробелов, между месяцами — длинное с
 * неразрывным пробелом перед ним (typography.md §4).
 */
export function dateRange(from: IsoDate, to: IsoDate): string {
  const a = parseIsoDate(from);
  const b = parseIsoDate(to);
  if (!a || !b) return `${from} — ${to}`;

  if (a.year === b.year && a.month === b.month) {
    return `${a.day}–${b.day} ${MONTHS_GENITIVE[a.month - 1]}`;
  }
  if (a.year === b.year) {
    return `${dayMonth(from)}${NBSP}— ${dayMonth(to)}`;
  }
  return `${longDate(from)}${NBSP}— ${longDate(to)}`;
}

/** Сколько дней в месяце. Февраль високосного года — 29. */
function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/**
 * Заголовок периода отчёта: «Июль 2026» или «18 апреля — 30 мая».
 *
 * Полный календарный месяц называется по имени: отчёты ежемесячные, и
 * «1–31 июля» читатель всё равно про себя переводит в «июль». Обрезанный
 * период остаётся диапазоном — иначе документ за три недели выдавал бы
 * себя за месячный.
 *
 * Год приписывается только к названию месяца: в диапазоне его уже несёт
 * `dateRange`, когда границы расходятся по годам.
 */
export function reportPeriodTitle(from: IsoDate, to: IsoDate): string {
  const start = parseIsoDate(from);
  const end = parseIsoDate(to);
  if (!start || !end) return dateRange(from, to);

  const wholeMonth =
    start.year === end.year &&
    start.month === end.month &&
    start.day === 1 &&
    end.day === daysInMonth(end.year, end.month);

  if (wholeMonth) return `${monthTitle(from)} ${start.year}`;

  return dateRange(from, to);
}

/**
 * Сколько целых дней от одной календарной даты до другой.
 *
 * Считается по UTC-полуночам, а не по локальным: в зоне с переводом часов
 * (Europe/Berlin и вся Европа восточнее нас включительно) в сутках перехода
 * 23 или 25 часов, и деление локальной разницы на 24 часа теряет день.
 */
export function daysBetween(from: IsoDate, to: IsoDate): number {
  const a = parseIsoDate(from);
  const b = parseIsoDate(to);
  if (!a || !b) return 0;
  const DAY_MS = 24 * 60 * 60 * 1000;
  const fromMs = Date.UTC(a.year, a.month - 1, a.day);
  const toMs = Date.UTC(b.year, b.month - 1, b.day);
  return Math.round((toMs - fromMs) / DAY_MS);
}

/* ─────────────────────────── величины ─────────────────────────── */

/** Разряды без единицы: `6451` → `6 451`. */
export function formatNumber(value: number): string {
  return value.toLocaleString(LOCALE);
}

/**
 * Деньги в валюте отчёта. `mixed` — кабинет с несколькими валютами: знак
 * опускается, потому что складывать разные валюты в один символ нельзя.
 */
export function money(value: number, currency: string): string {
  return buildFormatter(currency, LOCALE).format(value);
}

/**
 * Split an amount into the number and its unit so they can be typeset apart —
 * the figure large and bright, the unit small and muted. Every big number in
 * the product reads this way.
 */
export function moneyParts(value: number, currency: string): {value: string; unit: string} {
  const parts = buildFormatterObject(currency, LOCALE).format(value);
  return {value: parts.value.trim(), unit: parts.currency};
}

/**
 * Бюджет выборки — «32 млрд ₽».
 *
 * Полное число здесь не читается: тридцать два миллиарда рублей набраны
 * разрядами занимают полстроки и требуют считать нули. Это довод о порядке
 * величины, а не сумма, которую с чем-то сверяют.
 */
export function billions(value: number, currency: string): string {
  const scaled = Math.round((value / 1e9) * 10) / 10;
  const unit = moneyParts(0, currency).unit;
  const suffix = unit ? ` млрд${NBSP}${unit}` : ' млрд';
  return `${scaled.toLocaleString(LOCALE)}${suffix}`;
}

const UNIT_FORMS: Partial<Record<Unit, [string, string, string]>> = {
  ENQUIRY: ENQUIRIES,
  DAY: DAYS,
  POINT: POINTS,
};

/**
 * Величина словами: `{value: 26, unit: 'day', of: 128}` → «26 дней из 128».
 *
 * Знаменатель приписывается тем же предложением, а не отдельной подписью:
 * «26 дней» и «из 128» — одно утверждение, и разорванное на две строки оно
 * теряет смысл.
 */
export function formatAmount(amount: Amount, currency: string): string {
  const {value, unit, of} = amount;

  const head =
    unit === 'MONEY'
      ? money(value, currency)
      : unit === 'PERCENT'
        ? `${formatNumber(value)}${NBSP}%`
        : unit === 'COUNT'
          ? formatNumber(value)
          : pluralize(value, UNIT_FORMS[unit] ?? ENQUIRIES);

  if (of === undefined || of === null) return head;
  const tail = unit === 'MONEY' ? money(of, currency) : formatNumber(of);
  return `${head} из${NBSP}${tail}`;
}

/**
 * Значение в паре «было → стало». Качественная цель приходит текстом и
 * набирается как есть; измеримая — числом, а «не более» / «не менее»
 * добавляет граница.
 */
export function formatTarget(target: Target, currency: string): string {
  if (target.text) return target.text;
  if (!target.amount) return '';
  const value = formatAmount(target.amount, currency);
  if (target.bound === 'AT_MOST') return `не${NBSP}более ${value}`;
  if (target.bound === 'AT_LEAST') return `не${NBSP}менее ${value}`;
  return value;
}

/**
 * Читаемая мера строки — 9–11 слов, ~70 знаков.
 *
 * Карточка шириной в полэкрана вмещает вдвое больше, и текст,
 * растянутый на всю её ширину, читается заметно хуже: на возврате к началу
 * следующей строки глаз теряет место. Ограничение ставится на сам текстовый
 * блок, а не на карточку — карточке ширина нужна для цифр и таблиц.
 *
 * Считано по кеглю основного текста (`body.l`, 16px) на реальных абзацах
 * отчёта: 580px дают 10,2 слова в строке. Прежние 460px давали 7,9 — строка
 * рвалась чаще, чем того требует чтение, и абзац из трёх фраз занимал шесть
 * строк вместо четырёх.
 */
export const MEASURE = 580;

/**
 * Мера для вводного абзаца этажа — там строка длиннее уместна.
 *
 * Число другое, а слов столько же: вводный абзац набран крупнее (18px), и
 * при равной ширине в него влезло бы меньше. 700px на этом кегле дают
 * 11,2 слова — верх той же полосы, что и у основной меры.
 */
export const MEASURE_WIDE = 700;

/**
 * Ширина полосы документа. Общая для отчёта и хранилища: это один маршрут,
 * и колонка не должна прыгать при переходе между экранами.
 */
export const PAGE_MAX_WIDTH = 1440;

/**
 * Ширина полосы формы — уже документной.
 *
 * Документ раскладывает по ширине карточки, таблицы и колонки; форму
 * заполняют сверху вниз, и растянутое на 1180 поле ввода превращает
 * короткое значение в строку через весь экран, а подпись к нему уводит от
 * самого поля.
 */
export const FORM_MAX_WIDTH = 840;

/**
 * Заливка карточки — приглушённый `surface1`.
 *
 * Сплошной токен на длинной странице набирает слишком много веса: два
 * десятка карточек подряд читаются как светлые пятна, между которыми
 * теряется канва. Разбавляем прозрачностью через `color-mix`, чтобы
 * значение осталось привязанным к токену, а не превратилось в хардкод.
 */
export const CARD_FILL = `color-mix(in srgb, ${surfaceVar('surface1')} 55%, transparent)`;

/** То же для второй ступени — панелей внутри карточек. */
export const PANEL_FILL = `color-mix(in srgb, ${surfaceVar('surface2')} 45%, transparent)`;

/**
 * Заливка карточки под курсором — тот же токен, взятый плотнее.
 *
 * Ховер обязан читаться как «та же поверхность, только ближе», а не как
 * другой цвет: поэтому меняется одна прозрачность, а не токен. Соседний
 * `surface2` здесь не годится — он ступень иерархии, и карточка под
 * курсором начинала бы притворяться вложенной панелью.
 */
export const CARD_FILL_HOVER = `color-mix(in srgb, ${surfaceVar('surface1')} 85%, transparent)`;

/**
 * Lining, tabular figures. Any column of numbers — a score breakdown, a
 * month-by-month table, stacked amounts — needs digits of equal advance
 * width, otherwise the columns visibly shiver from row to row.
 */
export const TABULAR = {
  fontVariantNumeric: 'tabular-nums lining-nums',
  fontFeatureSettings: '"lnum" 1, "tnum" 1',
} as const;

/**
 * Signed delta with a real minus sign (U+2212), not a hyphen. The hyphen
 * reads as a dash at heading sizes and breaks the line badly.
 */
export function signed(value: number): string {
  if (value > 0) return `+${value}`;
  if (value < 0) return `−${Math.abs(value)}`;
  return '0';
}
